import { WaypointStatusName, MapSourceType } from '@caresend/types';
import { formatCentsToDollar } from '@caresend/utils';
import mapboxGl from 'mapbox-gl';
import { getPricePinSVG } from './getPinSVG.js';
import { MarkerIconName } from './model.js';
import { isPoint, isCoordinates } from './private/index.js';
import { HOME_MAP_ICON, MEDICAL_ICON, STORE_ICON, CLINICIAN_MAP_ICON, ARRIVAL_MAP_ICON, PLAY_MAP_ICON } from './private/assets/iconsUrls.js';
import { reportException } from '../../../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import { isPatientUser } from '../../../../store/helpers.js';

const getColorClassName = (waypointType) => {
    switch (waypointType) {
        case MapSourceType.DRAFT_WAYPOINTS:
        case MapSourceType.WAYPOINT:
            return 'MapView__marker-content--patient';
        case MapSourceType.PLACES:
            return 'MapView__marker-content--pick-drop';
        case MapSourceType.PICK_DROP:
            return 'MapView__marker-content--pick-drop';
        case MapSourceType.PATIENT:
            return 'MapView__marker-content--patient';
        case MapSourceType.ITINERARY_ACTUAL_START_POINT:
            return 'MapView__marker-content--actual-start-point';
        case MapSourceType.ITINERARY_INDICATED_START_POINT:
            return 'MapView__marker-content--indicated-start-point';
        case MapSourceType.CLINICIAN_LAST_LOCATION:
            return 'MapView__marker-content--clinician-last-location';
        case MapSourceType.ITINERARY_END_POINT:
            return 'MapView__marker-content--end-point';
        default:
            return 'MapView__marker-content';
    }
};
const getWaypointStatusIndicatorClassName = (waypointStatus) => {
    switch (waypointStatus) {
        case WaypointStatusName.AT_WAYPOINT:
            return 'MapView__marker--waypoint-status--at-waypoint';
        case WaypointStatusName.COMPLETE:
            return 'MapView__marker--waypoint-status--complete';
        default:
            return '';
    }
};
const addToDefaultMarker = (properties, markerElement) => {
    var _a, _b, _c, _d, _e, _f;
    const { marker, highlight } = properties;
    if (!marker)
        throw Error('Unexpected missing marker property');
    let waypointDisplayStatus;
    const isComplete = ((_a = properties.marker) === null || _a === void 0 ? void 0 : _a.waypointStatus) === WaypointStatusName.COMPLETE;
    const isAtWaypoint = ((_b = properties.marker) === null || _b === void 0 ? void 0 : _b.waypointStatus) === WaypointStatusName.AT_WAYPOINT;
    if (isComplete)
        waypointDisplayStatus = WaypointStatusName.COMPLETE;
    if (isAtWaypoint)
        waypointDisplayStatus = WaypointStatusName.AT_WAYPOINT;
    const { waypointLetter, waypointType, iconName } = marker;
    const contentElement = document.createElement('div');
    contentElement.classList.add('MapView__marker-content', 'MapView__marker-content--default');
    contentElement.classList.add(getColorClassName(waypointType));
    let iconSize = 18;
    let iconSrc = '';
    if (waypointType === MapSourceType.DRAFT_WAYPOINTS)
        iconSrc = MEDICAL_ICON;
    if (waypointType === MapSourceType.PLACES)
        iconSrc = STORE_ICON;
    if (iconName === MarkerIconName.CLINICIAN)
        iconSrc = CLINICIAN_MAP_ICON;
    if (iconName === MarkerIconName.ARRIVAL)
        iconSrc = ARRIVAL_MAP_ICON;
    if (iconName === MarkerIconName.PLAY) {
        iconSrc = PLAY_MAP_ICON;
        iconSize = 13;
    }
    if (iconName === MarkerIconName.HOME)
        iconSrc = HOME_MAP_ICON;
    if (waypointType === MapSourceType.USER_PHOTO) {
        const initials = (_c = properties.marker) === null || _c === void 0 ? void 0 : _c.initials;
        const markerSrc = (_d = properties.marker) === null || _d === void 0 ? void 0 : _d.markerSrc;
        if (markerSrc) {
            const img = document.createElement('img');
            img.src = markerSrc;
            img.classList.add('MapView__marker--user-photo');
            contentElement.appendChild(img);
            contentElement.style.backgroundColor = 'transparent';
        }
        else if (initials) {
            contentElement.classList.add('MapView__marker-content--initials');
            contentElement.innerText = initials;
            const color = (_f = (_e = properties.marker) === null || _e === void 0 ? void 0 : _e.color) !== null && _f !== void 0 ? _f : '#474D66'; // $cs-grey-800;
            contentElement.style.backgroundColor = color;
        }
    }
    else if (iconName) {
        const icon = document.createElement('img');
        icon.src = iconSrc;
        icon.width = iconSize;
        icon.height = iconSize;
        contentElement.appendChild(icon);
    }
    else if (waypointLetter) {
        contentElement.innerText = waypointLetter;
    }
    else if (iconSrc) {
        const icon = document.createElement('img');
        icon.src = iconSrc;
        icon.width = iconSize;
        icon.height = iconSize;
        contentElement.appendChild(icon);
    }
    if (highlight) {
        const highlightElement = document.createElement('div');
        highlightElement.innerText = ' ';
        highlightElement.classList.add('MapView__marker--highlight');
        contentElement.appendChild(highlightElement);
    }
    if (waypointDisplayStatus) {
        const waypointStatusIndicator = document.createElement('div');
        waypointStatusIndicator.classList.add('MapView__marker--waypoint-status');
        waypointStatusIndicator.classList.add(getWaypointStatusIndicatorClassName(waypointDisplayStatus));
        contentElement === null || contentElement === void 0 ? void 0 : contentElement.appendChild(waypointStatusIndicator);
    }
    markerElement.appendChild(contentElement);
};
const buildDefaultMarker = (sourceGroupData, map) => {
    /** Add inner content if any source in the group has a marker property */
    const shouldAddMarkerContent = sourceGroupData.some((data) => { var _a; return (_a = data.properties) === null || _a === void 0 ? void 0 : _a.marker; });
    const markerElement = document.createElement('div');
    markerElement.classList.add('MapView__marker', 'MapView__marker--default');
    if (shouldAddMarkerContent) {
        markerElement.classList.add('MapView__marker--waypoint');
    }
    sourceGroupData.forEach((data) => {
        var _a, _b, _c;
        if (!map || !isPoint(data.geometry))
            return;
        const { coordinates } = data.geometry;
        if (!isCoordinates(coordinates))
            return;
        if (((_a = data.properties) === null || _a === void 0 ? void 0 : _a.marker) && shouldAddMarkerContent) {
            addToDefaultMarker(data.properties, markerElement);
        }
        if ((_b = data.properties) === null || _b === void 0 ? void 0 : _b.label) {
            // Create a popup, but don't add it to the map yet.
            const popup = new mapboxGl.Popup({
                closeButton: false,
                closeOnClick: false,
            });
            // Populate the popup and set its coordinates
            popup.setLngLat(coordinates)
                .setHTML((_c = data.properties) === null || _c === void 0 ? void 0 : _c.label)
                .addTo(map);
        }
    });
    return markerElement;
};
const getPriceText = (price) => {
    if (price === undefined) {
        return '';
    }
    if (price > 0) {
        return `${formatCentsToDollar(price)}`;
    }
    return 'Free';
};
const getPricePinInnerContent = (properties) => {
    const innerContentElement = document.createElement('div');
    const { selected, marker } = properties;
    if (!marker)
        throw Error('Unexpected missing marker property');
    const { markerStyle, price } = marker;
    const useWhiteText = markerStyle !== 'price' || selected;
    innerContentElement.classList.add('MapView__marker-content__inner', 'MapView__marker-content__inner--price');
    if (useWhiteText) {
        innerContentElement.classList.add('MapView__marker-content__inner--white');
    }
    if (markerStyle === 'you' && !isPatientUser()) {
        // Show a house icon instead of 'You' to non-patient users
        const icon = document.createElement('img');
        const iconSize = 18;
        icon.src = HOME_MAP_ICON;
        icon.width = iconSize;
        icon.height = iconSize;
        innerContentElement.appendChild(icon);
    }
    else {
        const textElement = document.createElement('span');
        textElement.innerText = markerStyle === 'you' ? 'You' : getPriceText(price);
        innerContentElement.appendChild(textElement);
    }
    return innerContentElement;
};
/**
 * Grouping not supported. Pass `disableGrouping` prop if using.
 */
const buildPriceMarker = (sourceGroupData) => {
    var _a;
    if (sourceGroupData.length > 1) {
        reportException('Unexpected grouped sources passed to buildPriceMarker. Pin(s) will be '
            + 'dropped. Use the disableGrouping prop.');
    }
    const markerElement = document.createElement('div');
    markerElement.classList.add('MapView__marker', 'MapView__marker--price');
    const { properties } = (_a = sourceGroupData[0]) !== null && _a !== void 0 ? _a : {};
    if (!properties)
        throw Error('Unexpected missing properties');
    const { selected, marker } = properties;
    if (!marker)
        throw Error('Unexpected missing marker property');
    const { isClickable, markerStyle } = marker;
    const contentElement = document.createElement('div');
    contentElement.classList.add('MapView__marker-content', 'MapView__marker-content--price');
    const useShadow = markerStyle !== 'you';
    if (useShadow) {
        contentElement.classList.add('MapView__marker-content--shadow');
    }
    if (isClickable && !selected) {
        contentElement.classList.add('MapView__marker-content--clickable');
    }
    const svg = getPricePinSVG(properties);
    contentElement.appendChild(svg);
    const innerContentElement = getPricePinInnerContent(properties);
    contentElement.appendChild(innerContentElement);
    markerElement.appendChild(contentElement);
    return markerElement;
};

export { buildDefaultMarker, buildPriceMarker, getPriceText };
